import type { FallbackRender } from "@sentry/react";
import type { TFunction } from "i18next";
import { CircleAlert } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useSendFeedback } from "~/common/hooks";
import { Badge, Button } from "~/components/ui";

import { isApolloLikeError } from "./apolloError";

type ErrorTag = {
  id: string;
  label: string;
};

const isGqlValidationError = (error: unknown): boolean => {
  if (!isApolloLikeError(error)) return false;

  return (
    error.graphQLErrors?.some((e) => {
      if (typeof e === "object" && e !== null && "extensions" in e) {
        const extensions = (e as { extensions?: { code?: string } }).extensions;
        return extensions?.code === "validation-failed";
      }
      return false;
    }) ?? false
  );
};

// Labels are resolved per call so they follow a language switch. The key stays a literal here
// because the i18n extractor drops keys it cannot see statically.
const getErrorTags = (error: unknown, t: TFunction): ErrorTag[] => {
  const tags: ErrorTag[] = [];
  // Assume graphql validation error is due to permission misconfiguration
  if (isGqlValidationError(error)) {
    tags.push({ id: "permissionError", label: t("feature.errors.tags.permissionError") });
  }

  return tags;
};

export const ErrorFallback: FallbackRender = ({ error, resetError, eventId }: Parameters<FallbackRender>[0]) => {
  const { t } = useTranslation();
  const tags = getErrorTags(error, t);

  // showReportDialog は Slack アラートルールで拾えないため、モダン widget に切り替え
  const feedbackTags = useMemo(
    () => ({
      feedback_source: "error_report",
      ...(eventId ? { error_event_id: eventId } : {}),
    }),
    [eventId],
  );
  const { sendFeedback, isAvailable: isFeedbackAvailable } = useSendFeedback({
    tags: feedbackTags,
  });

  return (
    <div
      role="alert"
      className="mx-auto mt-8 flex w-[40vw] items-start gap-2.5 rounded-[var(--radius-md)] bg-danger-bg p-4 text-danger"
    >
      <CircleAlert className="mt-px size-4 shrink-0" aria-hidden />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <span className="text-14-bold">{t("feature.errors.errorFallback.title")}</span>
        <p className="text-14-regular">{t("feature.errors.errorFallback.message")}</p>

        {tags.length > 0 && (
          <div className="flex flex-wrap items-center gap-1">
            {tags.map((tag) => (
              <Badge key={tag.id} className="bg-danger text-fg-inverse">
                {tag.label}
              </Badge>
            ))}
          </div>
        )}

        {/* Opaque, not a tint: a translucent fill composites over the alert and drops the message
            below the 4.5:1 contrast this text needs. */}
        <div className="flex flex-col gap-2 rounded-[var(--radius-xs)] bg-white p-3">
          <p className="text-14-regular capitalize">{t("feature.errors.errorFallback.errorDetails")}</p>
          {/* Error and GraphQL messages arrive with embedded newlines and unbreakable identifiers */}
          <p className="whitespace-pre-line break-words text-14-regular">
            {error instanceof Error ? error.message : String(error)}
          </p>
        </div>

        <hr className="border-0 border-t border-danger/20" />

        <div className="flex flex-wrap items-center gap-2">
          {isFeedbackAvailable && (
            <Button
              variant="danger-outline"
              size="sm"
              // danger-outline borders in --score-c-bg, which is this alert's own background. Opaque
              // rather than tinted so the outline clears 3:1 against both the fill and the alert.
              className="border-danger capitalize"
              onClick={() => void sendFeedback()}
            >
              {t("ui.reportIssue")}
            </Button>
          )}
          <span className="text-14-regular">{t("ui.or")}</span>
          <button
            type="button"
            // Inherits the alert's colour because the link palette does not reach 4.5:1 on this
            // background, so the underline is what marks it as actionable.
            className="cursor-pointer text-14-regular capitalize underline"
            onClick={() => resetError()}
          >
            {t("ui.hide")}
          </button>
        </div>
      </div>
    </div>
  );
};
